import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { useLanguage } from "../context/LanguageContext";
import { sectionPath } from "../utils/routePaths";
import { resolveSlug } from "../utils/slugify";
import { optimizedImageUrl } from "../utils/cloudinaryUrl";

const ACCENT = "#C47C2B";
const DARK = "#1A1A18";



const stripHtml = (html) => {
  if (!html) return "";
  return html
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/\s+/g, " ")
    .trim();
};

const shuffle = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

export default function TopDestinations() {
  const [places, setPlaces] = useState([]);
  const [categories, setCategories] = useState({});
  const [loading, setLoading] = useState(true);
  const { language, t } = useLanguage();

  useEffect(() => {
    Promise.all([
      getDocs(collection(db, "places")),
      getDocs(collection(db, "categories")),
    ])
      .then(([placesSnap, catsSnap]) => {
        const all = placesSnap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((p) => p.image);
        setPlaces(shuffle(all).slice(0, 8));
        const catMap = {};
        catsSnap.docs.forEach((d) => {
          const data = d.data();
          catMap[data.name] = {
            icon: data.icon || "location_on",
            nameGeo: data.nameGeo || data.name,
          };
        });
        setCategories(catMap);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (!loading && places.length === 0) return null;

  const getName = (p) => (language === "ka" && p.nameKa ? p.nameKa : p.name);
  const getDesc = (p) =>
    stripHtml(
      language === "ka" && p.descriptionKa ? p.descriptionKa : p.description,
    );

  // helpers for cleaner JSX
  const card = (idx, size) => {
    if (loading) return <Skeleton size={size} />;
    const p = places[idx];
    if (!p) return <Skeleton size={size} />;
    const catKey = p.category || "";
    const catData = categories[catKey] || {};
    const tag = language === "ka" ? catData.nameGeo || catKey : catKey;
    return (
      <DestCard
        place={p}
        name={getName(p)}
        desc={getDesc(p)}
        tag={tag}
        tagColor="#C47C2B"
        tagIcon={catData.icon || "location_on"}
        language={language}
        size={size}
      />
    );
  };

  return (
    <section
      style={{
        background: "transparent",
        padding: "clamp(40px, 6vw, 80px) clamp(20px, 6vw, 80px)",
      }}
    >
      {/* ── DESKTOP LAYOUT ─────────────────────────────────────── */}
      <div className="td-desktop">
        {/* ROW 1: text panel · card[0] · card[1]
            ≥1400px adds card[2] as a 4th column */}
        <div className="td-row1">
          {/* Text panel */}
          <div className="td-text-panel">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                marginBottom: 12,
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path
                  d="M2 22L10 8L14 14L18 10L22 22Z"
                  stroke={ACCENT}
                  strokeWidth="2.2"
                  strokeLinejoin="round"
                  fill="none"
                />
              </svg>
              <span
                style={{
                  fontFamily: "Geist, sans-serif",
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  color: "var(--accent-text)",
                }}
              >
                {language === "ka" ? "პოპულარული ადგილები" : "Top Destinations"}
              </span>
            </div>
            <h2
              style={{
                fontFamily: "Geist, sans-serif",
                fontSize: "clamp(22px, 2vw, 32px)",
                fontWeight: 800,
                letterSpacing: "-0.03em",
                color: "var(--text-primary)",
                lineHeight: 1.1,
                marginBottom: 12,
              }}
            >
              {t("home_top_title") ||
                (language === "ka" ? (
                  "სვანეთის გამორჩეული ადგილები"
                ) : (
                  <>
                    Places you
                    <br />
                    can't miss
                  </>
                ))}
            </h2>
            <p
              style={{
                fontFamily: "Geist, sans-serif",
                fontSize: 13,
                color: "var(--text-secondary)",
                lineHeight: 1.6,
                marginBottom: 24,
              }}
            >
              {t("home_top_subtitle") ||
                (language === "ka"
                  ? "აღმოჩინე სვანეთის ყველაზე ლამაზი ადგილები"
                  : "Discover the most beautiful places in Svaneti.")}
            </p>
            <Link
              to={sectionPath(language, "categories")}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                background: "#2D4A3E",
                color: "#fff",
                padding: "10px 16px",
                borderRadius: 10,
                fontFamily: "Geist, sans-serif",
                fontSize: 13,
                fontWeight: 600,
                textDecoration: "none",
                transition: "background 0.2s",
                width: "fit-content",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = "#3a5c4e")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background = "#2D4A3E")
              }
            >
              {t("featured_view_all") ||
                (language === "ka" ? "ყველა" : "View All")}
              <span
                className="material-symbols-outlined"
                style={{ fontSize: 16 }}
              >
                arrow_forward
              </span>
            </Link>
          </div>

          {/* card[0] */}
          {card(0, "tall")}
          {/* card[1] */}
          {card(1, "tall")}
          {/* card[2] — 4th col on wide screens only */}
          <div className="td-wide-only">{card(2, "tall")}</div>
        </div>

        {/* ROW 2: always 3 full cards + 1 stacked column (4 visual columns)
            On normal screens: card[2] · card[3] · card[4] | stacked[5+6]
            On wide screens:   card[3] · card[4] · card[5] | stacked[6+7]
            We achieve this with two sets and CSS show/hide */}

        {/* Normal row2 (< 1400px): columns = card2, card3, card4, stacked(5+6) */}
        <div className="td-row2 td-normal-row2">
          {card(2, "mid")}
          {card(3, "mid")}
          {card(4, "mid")}
          {/* stacked column */}
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {card(5, "micro")}
            {card(6, "micro")}
          </div>
        </div>

        {/* Wide row2 (≥ 1400px): card2 is already in row1, so shift by 1 */}
        <div className="td-row2 td-wide-row2">
          {card(3, "mid")}
          {card(4, "mid")}
          {card(5, "mid")}
          {/* stacked column */}
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {card(6, "micro")}
            {card(7, "micro")}
          </div>
        </div>
      </div>

      {/* ── MOBILE LAYOUT ──────────────────────────────────────── */}
      <div className="td-mobile">
        <div style={{ marginBottom: 20 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              marginBottom: 8,
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path
                d="M2 22L10 8L14 14L18 10L22 22Z"
                stroke={ACCENT}
                strokeWidth="2.2"
                strokeLinejoin="round"
                fill="none"
              />
            </svg>
            <span
              style={{
                fontFamily: "Geist, sans-serif",
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: "var(--accent-text)",
              }}
            >
              {language === "ka" ? "პოპულარული ადგილები" : "Top Destinations"}
            </span>
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <h2
              style={{
                fontFamily: "Geist, sans-serif",
                fontSize: 22,
                fontWeight: 800,
                letterSpacing: "-0.03em",
                color: "var(--text-primary)",
                lineHeight: 1.15,
                margin: 0,
              }}
            >
              {t("home_top_title") ||
                (language === "ka"
                  ? "სვანეთის გამორჩეული ადგილები"
                  : "Places you can't miss")}
            </h2>
            <Link
              to={sectionPath(language, "categories")}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                background: "#2D4A3E",
                color: "#fff",
                padding: "8px 14px",
                borderRadius: 10,
                fontFamily: "Geist, sans-serif",
                fontSize: 12,
                fontWeight: 600,
                textDecoration: "none",
                whiteSpace: "nowrap",
                marginLeft: 12,
                flexShrink: 0,
              }}
            >
              {t("featured_view_all") ||
                (language === "ka" ? "ყველა" : "View All")}
              <span
                className="material-symbols-outlined"
                style={{ fontSize: 14 }}
              >
                arrow_forward
              </span>
            </Link>
          </div>
        </div>

        <div className="td-mobile-grid">
          {(loading ? Array(6).fill(null) : places.slice(0, 6)).map(
            (place, i) =>
              loading || !place ? (
                <MobileCardSkeleton key={i} />
              ) : (
                (() => {
                  const catKey = place.category || "";
                  const catData = categories[catKey] || {};
                  const tag =
                    language === "ka" ? catData.nameGeo || catKey : catKey;
                  return (
                    <MobileCard
                      key={place.id}
                      place={place}
                      name={getName(place)}
                      desc={getDesc(place)}
                      tag={tag}
                      tagColor="#C47C2B"
                      tagIcon={catData.icon || "location_on"}
                      language={language}
                    />
                  );
                })()
              ),
          )}
        </div>
      </div>

      <style>{`
        @keyframes tdShimmer { 0%,100%{opacity:.6} 50%{opacity:1} }

        .td-desktop { display: block; }
        .td-mobile  { display: none; }

        /* ── Row 1: 3 cols default ── */
        .td-row1 {
          display: grid;
          grid-template-columns: 240px 1fr 1fr;
          gap: 12px;
          margin-bottom: 12px;
          align-items: stretch;
        }
        .td-text-panel {
          display: flex;
          flex-direction: column;
          justify-content: center;
        }

        /* ── Row 2: always 4 cols (3 cards + 1 stacked) ── */
        .td-row2 {
          display: grid;
          grid-template-columns: 1fr 1fr 1fr 1fr;
          gap: 12px;
        }

        /* By default: show normal row2, hide wide row2 */
        .td-wide-only  { display: none; }
        .td-wide-row2  { display: none; }
        .td-normal-row2 { display: grid; }

        /* ── Wide screens ≥ 1400px ── */
        @media (min-width: 1400px) {
          /* Row 1 gets a 4th card column */
          .td-row1 { grid-template-columns: 250px 1fr 1fr 1fr; }
          .td-wide-only { display: block; }
          /* Swap row2 variants */
          .td-normal-row2 { display: none; }
          .td-wide-row2   { display: grid; }
        }

        /* ── Mobile ── */
        @media (max-width: 640px) {
          .td-desktop { display: none; }
          .td-mobile  { display: block; }
          .td-mobile-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 12px;
          }
        }
      `}</style>
    </section>
  );
}

/* ── Skeleton ─────────────────────────────────────────────────── */
function Skeleton({ size }) {
  const h = size === "micro" ? 100 : size === "mid" ? 200 : 230;
  return (
    <div
      style={{
        borderRadius: 14,
        background: "var(--bg-surface-alt)",
        height: h,
        animation: "tdShimmer 1.5s infinite",
      }}
    />
  );
}

/* ── Desktop card ─────────────────────────────────────────────── */
// size: "tall" (row1, 230px) | "mid" (row2 full, 200px) | "micro" (stacked, 100px)
function DestCard({
  place,
  name,
  desc,
  tag,
  tagColor,
  tagIcon,
  language,
  size,
}) {
  const [hovered, setHovered] = useState(false);

  const isMicro = size === "micro";
  const isMid = size === "mid";

  const height = isMicro ? 100 : isMid ? 200 : 230;
  const padV = isMicro ? 8 : 14;
  const padH = isMicro ? 12 : 16;
  const fontSize = isMicro ? 13 : isMid ? 17 : 19;
  const btnSize = isMicro ? 26 : 34;
  const btnIcon = isMicro ? 13 : 16;

  return (
    <Link
      to={sectionPath(language, "place", resolveSlug(place, language))}
      style={{
        borderRadius: 14,
        overflow: "hidden",
        display: "block",
        position: "relative",
        background: DARK,
        textDecoration: "none",
        height,
        transform: hovered ? "translateY(-3px)" : "translateY(0)",
        transition: "transform 0.3s, box-shadow 0.3s",
        boxShadow: hovered
          ? "0 14px 36px rgba(0,0,0,0.22)"
          : "0 4px 14px rgba(0,0,0,0.1)",
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <img
        src={optimizedImageUrl(place.image, 900)}
        alt={name}
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          transform: hovered ? "scale(1.06)" : "scale(1)",
          transition: "transform 0.5s ease",
        }}
      />

      <div
        style={{
          position: "absolute",
          inset: 0,
          background: isMicro
            ? "linear-gradient(to top, rgba(5,8,15,0.88) 0%, rgba(5,8,15,0.2) 80%, transparent 100%)"
            : "linear-gradient(to top, rgba(5,8,15,0.82) 0%, rgba(5,8,15,0.12) 50%, transparent 100%)",
        }}
      />

      {/* Tag */}
      <div
        style={{
          position: "absolute",
          top: isMicro ? 8 : 10,
          left: isMicro ? 8 : 10,
          display: "flex",
          alignItems: "center",
          gap: isMicro ? 3 : 4,
          background: tagColor,
          color: "#fff",
          padding: isMicro ? "3px 7px" : "4px 9px",
          borderRadius: 999,
          fontFamily: "Geist, sans-serif",
          fontSize: isMicro ? 10 : 11,
          fontWeight: 700,
        }}
      >
        <span
          className="material-symbols-outlined"
          style={{
            fontSize: isMicro ? 11 : 12,
            fontVariationSettings: "'FILL' 1",
          }}
        >
          {tagIcon}
        </span>
        {tag}
      </div>

      {/* Bottom info */}
      <div
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          padding: `${padV}px ${padH}px`,
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
        }}
      >
        <div style={{ flex: 1, marginRight: 8 }}>
          {!isMicro && !isMid && place.location && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                marginBottom: 3,
              }}
            >
              <span
                className="material-symbols-outlined"
                style={{ fontSize: 12, color: "rgba(255,255,255,0.55)" }}
              >
                location_on
              </span>
              <span
                style={{
                  fontFamily: "Geist, sans-serif",
                  fontSize: 11,
                  color: "rgba(255,255,255,0.55)",
                }}
              >
                {place.location}
              </span>
            </div>
          )}
          <h3
            style={{
              fontFamily: "Geist, sans-serif",
              fontSize,
              fontWeight: 700,
              color: "#fff",
              marginBottom: !isMicro && desc ? 4 : 0,
              lineHeight: 1.2,
              letterSpacing: "-0.01em",
            }}
          >
            {name}
          </h3>
          {!isMicro && desc && (
            <p
              style={{
                fontFamily: "Geist, sans-serif",
                fontSize: 12,
                color: "rgba(255,255,255,0.6)",
                margin: 0,
                lineHeight: 1.4,
                display: "-webkit-box",
                WebkitLineClamp: 1,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {desc}
            </p>
          )}
        </div>
        <div
          style={{
            width: btnSize,
            height: btnSize,
            borderRadius: "50%",
            background: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            transform: hovered ? "scale(1.1)" : "scale(1)",
            transition: "transform 0.2s",
          }}
        >
          <span
            className="material-symbols-outlined"
            style={{ fontSize: btnIcon, color: DARK }}
          >
            arrow_forward
          </span>
        </div>
      </div>
    </Link>
  );
}

/* ── Mobile skeleton ──────────────────────────────────────────── */
function MobileCardSkeleton() {
  return (
    <div>
      <div
        style={{
          borderRadius: 12,
          background: "var(--bg-surface-alt)",
          height: 130,
          marginBottom: 8,
          animation: "tdShimmer 1.5s infinite",
        }}
      />
      <div
        style={{
          height: 12,
          background: "var(--bg-surface-alt)",
          borderRadius: 6,
          marginBottom: 5,
          width: "60%",
          animation: "tdShimmer 1.5s infinite",
        }}
      />
      <div
        style={{
          height: 10,
          background: "var(--bg-surface-alt)",
          borderRadius: 6,
          width: "80%",
          animation: "tdShimmer 1.5s infinite",
        }}
      />
    </div>
  );
}

/* ── Mobile card ──────────────────────────────────────────────── */
function MobileCard({ place, name, desc, tag, tagColor, tagIcon, language }) {
  const [hovered, setHovered] = useState(false);

  return (
    <Link
      to={sectionPath(language, "place", resolveSlug(place, language))}
      style={{ display: "block", textDecoration: "none" }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div
        style={{
          position: "relative",
          borderRadius: 12,
          overflow: "hidden",
          height: 130,
          marginBottom: 8,
          background: DARK,
        }}
      >
        <img
          src={optimizedImageUrl(place.image, 400)}
          alt={name}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            transform: hovered ? "scale(1.05)" : "scale(1)",
            transition: "transform 0.4s ease",
          }}
        />
        <div
          style={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(to top, rgba(5,8,15,0.3) 0%, transparent 60%)",
          }}
        />
        <div
          style={{
            position: "absolute",
            top: 8,
            left: 8,
            display: "flex",
            alignItems: "center",
            gap: 3,
            background: tagColor,
            color: "#fff",
            padding: "3px 7px",
            borderRadius: 999,
            fontFamily: "Geist, sans-serif",
            fontSize: 10,
            fontWeight: 700,
          }}
        >
          <span
            className="material-symbols-outlined"
            style={{ fontSize: 11, fontVariationSettings: "'FILL' 1" }}
          >
            {tagIcon}
          </span>
          {tag}
        </div>
      </div>

      <h3
        style={{
          fontFamily: "Geist, sans-serif",
          fontSize: 13,
          fontWeight: 700,
          color: "var(--text-primary)",
          margin: "0 0 3px",
          lineHeight: 1.25,
        }}
      >
        {name}
      </h3>
      {desc && (
        <p
          style={{
            fontFamily: "Geist, sans-serif",
            fontSize: 11,
            color: "var(--text-secondary)",
            margin: 0,
            lineHeight: 1.4,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {desc}
        </p>
      )}
    </Link>
  );
}
